import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, apiFile, US_STATES, formatCurrency } from "../lib/api";

import { useAuth } from "../lib/auth";
import { useEntity } from "../lib/entity";
import Layout from "../components/Layout";
import {
  FileDown, FileSpreadsheet, Trash2, RefreshCw, Upload, Users, Store,
  Archive, Layers, Mail, Plus, CheckCircle2, AlertCircle, Send, ShieldCheck,
  Car, Receipt, FileText, ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import { Field } from "./Properties";
import HelpTip from "../components/Help/HelpTip";


export default function Settings() {
  const { can } = useAuth();
  const { entities, activeEntityId, reloadEntities } = useEntity();
  const today = new Date().toISOString().slice(0, 10);
  const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10);
  const [from, setFrom] = useState(yearStart);
  const [to, setTo] = useState(today);
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [downloadingPkg, setDownloadingPkg] = useState(false);
  const [properties, setProperties] = useState([]);
  const [csvProp, setCsvProp] = useState("");
  const [tenants, setTenants] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [improvements, setImprovements] = useState([]);
  const [newTenant, setNewTenant] = useState({ name: "", email: "", phone: "", property_id: "", rent_due_day: 1 });
  const [newVendor, setNewVendor] = useState({ name: "", category: "" });

  // Multi-LLC states
  const [newEntity, setNewEntity] = useState({ name: "", ein: "", state: "", address: "" });
  const [savingEntity, setSavingEntity] = useState(false);

  // Email Notification states
  const [notificationSettings, setNotificationSettings] = useState({
    is_configured: false,
    notify_rent_due: "true",
    notify_rent_overdue: "true",
    notify_lease_expiry: "true",
    notify_maintenance: "true",
  });
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [pRes, tRes, vRes, ciRes] = await Promise.all([
        api.get("/properties"),
        api.get("/tenants"),
        api.get("/vendors"),
        api.get("/capital-improvements").catch(() => ({ data: [] })),
      ]);
      setProperties(pRes.data || []);
      if (pRes.data[0]) setCsvProp(pRes.data[0].id);
      setTenants(tRes.data || []);
      setVendors(vRes.data || []);
      setImprovements(ciRes.data || []);

      if (can.admin) {
        const [sRes, lRes] = await Promise.all([
          api.get("/notifications/settings"),
          api.get("/notifications/log"),
        ]);
        if (sRes.data) setNotificationSettings((prev) => ({ ...prev, ...sRes.data }));
        setEmailLogs(lRes.data || []);
      }
    } catch (err) {
      console.error("Failed to load settings data:", err);
    }
  }, [can.admin]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Tax package download
  const downloadTaxPackage = async () => {
    setDownloadingPkg(true);
    try {
      const entityParam = activeEntityId !== "all" ? `&entity_id=${activeEntityId}` : "";
      const r = await apiFile.get(`/tax-package?year=${taxYear}${entityParam}`);
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Lapen_Holdings_Tax_Year_${taxYear}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Tax Package ZIP downloaded for ${taxYear}`);
    } catch {
      toast.error("Failed to generate tax package");
    } finally {
      setDownloadingPkg(false);
    }
  };

  const exportPdf = async () => {
    try {
      const r = await apiFile.get(`/pdf/tax-export?date_from=${from}&date_to=${to}`);
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `schedule-e-${from}-${to}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Schedule E PDF downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const exportMileagePdf = async () => {
    try {
      const r = await apiFile.get(`/pdf/mileage-log?date_from=${from}&date_to=${to}`);
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mileage-log-${from}-${to}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Mileage Log PDF downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const exportTxDetailPdf = async () => {
    try {
      const r = await apiFile.get(`/pdf/transaction-detail?date_from=${from}&date_to=${to}`);
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `transaction-detail-${from}-${to}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Transaction Detail PDF downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const exportCsv = async () => {
    try {
      const r = await apiFile.get(`/csv/tax-export?date_from=${from}&date_to=${to}`);
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `schedule-e-${from}-${to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  // Add Multi-LLC Entity
  const handleAddEntity = async (e) => {
    e.preventDefault();
    if (!newEntity.name) return;
    setSavingEntity(true);
    try {
      await api.post("/entities", newEntity);
      toast.success(`Entity "${newEntity.name}" created`);
      setNewEntity({ name: "", ein: "", state: "", address: "" });
      reloadEntities();
    } catch (err) {
      toast.error("Failed to create entity");
    } finally {
      setSavingEntity(false);
    }
  };

  const handleDeleteEntity = async (eid) => {
    if (!window.confirm("Delete this LLC entity? Properties assigned to it will be unassigned.")) return;
    try {
      await api.delete(`/entities/${eid}`);
      toast.success("Entity removed");
      reloadEntities();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cannot delete entity");
    }
  };

  // Save Notification Preferences
  const handleSaveNotifications = async (e) => {
    e.preventDefault();
    setSavingNotifications(true);
    try {
      await api.post("/notifications/settings", {
        notify_rent_due: notificationSettings.notify_rent_due,
        notify_rent_overdue: notificationSettings.notify_rent_overdue,
        notify_lease_expiry: notificationSettings.notify_lease_expiry,
        notify_maintenance: notificationSettings.notify_maintenance,
      });
      toast.success("Email notification preferences saved");
    } catch {
      toast.error("Failed to save notification preferences");
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleSendTestEmail = async (e) => {
    e.preventDefault();
    if (!testEmail) return;
    setSendingTest(true);
    try {
      await api.post("/notifications/test-email", { to_email: testEmail });
      toast.success(`Test email sent successfully to ${testEmail}!`);
      const lRes = await api.get("/notifications/log");
      setEmailLogs(lRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to deliver test email. Check logs below.");
      const lRes = await api.get("/notifications/log");
      setEmailLogs(lRes.data || []);
    } finally {
      setSendingTest(false);
    }
  };

  const addTenant = async (e) => {
    e.preventDefault();
    if (!newTenant.name) return;
    await api.post("/tenants", { ...newTenant, property_id: newTenant.property_id || null });
    setNewTenant({ name: "", email: "", phone: "", property_id: "" });
    loadAll();
    toast.success("Tenant added");
  };

  const addVendor = async (e) => {
    e.preventDefault();
    if (!newVendor.name) return;
    await api.post("/vendors", newVendor);
    setNewVendor({ name: "", category: "" });
    loadAll();
    toast.success("Vendor added");
  };

  const reseed = async () => {
    if (!window.confirm("Reset database with clean demo properties & transactions?")) return;
    await api.post("/settings/reseed");
    toast.success("Demo dataset reseeded");
    loadAll();
  };

  const wipe = async () => {
    if (!window.confirm("PERMANENT ACTION: Wipe all properties and transactions?")) return;
    await api.post("/settings/wipe");
    toast.success("Database wiped");
    loadAll();
  };

  return (
    <Layout title="Settings & Tax Center" subtitle="Multi-LLC management, email alerts, accountant package & IRS exports">
      {/* 1-Click Accountant Package */}
      <section className="card p-6 mb-6 border-teal-500/30 bg-teal-500/5" data-testid="tax-package-card">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1C3A5E] to-[#0D9488] text-white flex items-center justify-center shadow-md">
            <Archive size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold">1-Click Accountant Tax Package (ZIP)</h3>
              <HelpTip topic="schedule-e" />
            </div>
            <p className="text-xs text-[var(--muted)]">
              Complete tax bundle for CPA: Schedule E summary, individual property P&Ls, receipt index & rent rolls.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-[var(--border)]">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-[var(--text-secondary)]">Tax Year:</label>
            <select
              value={taxYear}
              onChange={(e) => setTaxYear(parseInt(e.target.value))}
              className="lapen-input !w-auto text-xs py-1.5 font-bold tabular-nums"
            >
              {[2026, 2025, 2024, 2023].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <button
            onClick={downloadTaxPackage}
            disabled={downloadingPkg}
            className="btn-primary text-xs py-2 px-4 ml-auto"
            data-testid="download-tax-package-btn"
          >
            <Archive size={15} />
            <span>{downloadingPkg ? "Generating Package..." : `Download Tax Year ${taxYear} ZIP`}</span>
          </button>
        </div>
      </section>

      {/* Multi-LLC Support Management */}
      <section className="card p-6 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Layers className="text-teal-400" size={20} />
          <h3 className="text-lg font-bold">Legal Entities & LLC Management</h3>
          <HelpTip topic="multi-llc" />
        </div>

        <p className="text-xs text-[var(--muted)] mb-4">
          Organize properties under distinct LLC holding companies for liability protection and segregated accounting.
        </p>

        {/* Add Entity Form */}
        {can.edit && (
          <form onSubmit={handleAddEntity} className="grid grid-cols-1 sm:grid-cols-4 gap-3 p-4 rounded-2xl bg-[var(--surface-hover)] border border-[var(--border)] mb-4">
            <Field label="LLC Legal Name *" value={newEntity.name} onChange={(v) => setNewEntity({ ...newEntity, name: v })} required />
            <Field label="EIN (Tax ID)" value={newEntity.ein} onChange={(v) => setNewEntity({ ...newEntity, ein: v })} placeholder="XX-XXXXXXX" />
            <div>
              <label className="label">State of Filing</label>
              <select
                className="lapen-input"
                value={newEntity.state}
                onChange={(e) => setNewEntity({ ...newEntity, state: e.target.value })}
              >
                <option value="">Select State...</option>
                {US_STATES.map((st) => (
                  <option key={st.code} value={st.code}>{st.code} — {st.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">

              <button type="submit" disabled={savingEntity} className="btn-primary w-full text-xs py-2">
                <Plus size={15} /> Add Entity
              </button>
            </div>
          </form>
        )}

        {/* Existing Entities List */}
        <div className="divide-y divide-[var(--border)]">
          {entities.map((e) => (
            <div key={e.id} className="py-3 flex items-center justify-between text-xs sm:text-sm">
              <div>
                <div className="font-bold flex items-center gap-2">
                  <span>{e.name}</span>
                  {e.state && <span className="tag tag-muted">{e.state}</span>}
                </div>
                <div className="text-xs text-[var(--muted)] mt-0.5">
                  EIN: {e.ein || "—"} · Properties: {e.property_count || 0}
                </div>
              </div>

              {can.admin && entities.length > 1 && (
                <button
                  onClick={() => handleDeleteEntity(e.id)}
                  className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10"
                  title="Delete entity"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Email Notification Preferences */}
      {can.admin && (
        <section className="card p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <Mail className="text-teal-400" size={20} />
              <h3 className="text-lg font-bold">Email Notification Preferences</h3>
              <HelpTip topic="email-notifications" />
            </div>
            {notificationSettings.is_configured ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                Platform Mail Delivery: Active
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                Mail Server Not Configured by Super Admin
              </span>
            )}
          </div>

          <p className="text-xs text-[var(--muted)] mb-4">
            Outgoing mail is handled securely through the platform-level mail server configured in Super Admin. Choose which automated event notifications are dispatched for your properties and leases.
          </p>

          <form onSubmit={handleSaveNotifications} className="space-y-4">
            {/* Notification Toggles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-3.5 rounded-lg bg-[var(--surface-subtle)] border border-[var(--border)]">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={notificationSettings.notify_rent_due === "true"}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_rent_due: e.target.checked ? "true" : "false" })}
                  className="rounded border-[var(--border)] text-teal-500 focus:ring-teal-400"
                />
                <span className="font-medium">Rent Due Reminder</span>
              </label>

              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={notificationSettings.notify_rent_overdue === "true"}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_rent_overdue: e.target.checked ? "true" : "false" })}
                  className="rounded border-[var(--border)] text-teal-500 focus:ring-teal-400"
                />
                <span className="font-medium">Overdue Rent Alert</span>
              </label>

              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={notificationSettings.notify_lease_expiry === "true"}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_lease_expiry: e.target.checked ? "true" : "false" })}
                  className="rounded border-[var(--border)] text-teal-500 focus:ring-teal-400"
                />
                <span className="font-medium">Lease Expiration</span>
              </label>

              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={notificationSettings.notify_maintenance === "true"}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_maintenance: e.target.checked ? "true" : "false" })}
                  className="rounded border-[var(--border)] text-teal-500 focus:ring-teal-400"
                />
                <span className="font-medium">Maintenance Orders</span>
              </label>
            </div>

            <div className="flex justify-end pt-2">
              <button type="submit" disabled={savingNotifications} className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5">
                <CheckCircle2 size={15} /> {savingNotifications ? "Saving..." : "Save Preferences"}
              </button>
            </div>
          </form>

          {/* Test Email Section */}
          <div className="mt-6 pt-4 border-t border-[var(--border)]">
            <h4 className="text-xs uppercase tracking-wider text-[var(--muted)] font-bold mb-2">Send Test Alert</h4>
            <p className="text-xs text-[var(--muted)] mb-3">
              Verify platform mail delivery and receipt at your address.
            </p>
            <form onSubmit={handleSendTestEmail} className="flex gap-2 max-w-md">
              <input
                type="email"
                required
                placeholder="recipient@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="lapen-input text-xs flex-1"
              />
              <button
                type="submit"
                disabled={sendingTest || !notificationSettings.is_configured}
                title={!notificationSettings.is_configured ? "Super Admin has not yet configured SMTP credentials" : "Send test email"}
                className="btn-secondary text-xs py-2 px-3 shrink-0 flex items-center gap-1.5"
              >
                <Send size={14} /> {sendingTest ? "Sending..." : "Send Test"}
              </button>
            </form>
          </div>

          {/* Email Delivery Logs */}
          {emailLogs.length > 0 && (
            <div className="mt-6 pt-4 border-t border-[var(--border)]">
              <h4 className="text-xs uppercase tracking-wider text-[var(--muted)] font-bold mb-3">Recent Email Delivery Log</h4>
              <div className="divide-y divide-[var(--border)] max-h-48 overflow-y-auto text-xs">
                {emailLogs.slice(0, 5).map((l) => (
                  <div key={l.id} className="py-2 flex items-center justify-between">
                    <div>
                      <div className="font-semibold">{l.subject}</div>
                      <div className="text-[var(--muted)] text-[11px]">To: {l.to_email} · {new Date(l.sent_at).toLocaleString()}</div>
                    </div>
                    <span className={`tag ${l.status === "sent" ? "tag-success" : "tag-danger"}`}>
                      {l.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Annual Depreciation Summary (Form 4562) */}
      <section className="card p-6 mb-6" data-testid="depreciation-summary-card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-500/10 text-teal-400 flex items-center justify-center">
              <ShieldCheck size={18} />
            </div>
            <div>
              <h3 className="text-base font-bold flex items-center gap-2">
                <span>Annual Depreciation Summary</span>
                <span className="tag tag-teal text-[10px]">Form 4562 Preparation</span>
              </h3>
              <p className="text-xs text-[var(--muted)]">
                Provide this to your CPA for IRS Form 4562 (Depreciation and Amortization) preparation.
              </p>
            </div>
          </div>
          <Link
            to="/improvements"
            className="text-xs text-teal-400 hover:text-teal-300 font-semibold flex items-center gap-1 self-start sm:self-auto"
          >
            Manage Capital Improvements <ArrowRight size={13} />
          </Link>
        </div>

        {/* 4 Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 my-4">
          <div className="glass-card p-3.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-600 dark:text-slate-400 font-semibold">Total Capital Assets</div>
            <div className="text-lg font-bold tabular-nums text-[var(--text)] mt-1">
              {formatCurrency(improvements.reduce((s, i) => s + (Number(i.cost) || 0), 0))}
            </div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 font-medium">{improvements.length} assets logged</div>
          </div>
          <div className="glass-card p-3.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-600 dark:text-slate-400 font-semibold">{taxYear} Annual Depreciation</div>
            <div className="text-lg font-bold tabular-nums text-teal-600 dark:text-teal-400 mt-1">
              {formatCurrency(improvements.reduce((s, i) => s + (Number(i.annual_depreciation) || 0), 0))}
            </div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 font-medium">Form 4562 deduction</div>
          </div>
          <div className="glass-card p-3.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-600 dark:text-slate-400 font-semibold">Accumulated To Date</div>
            <div className="text-lg font-bold tabular-nums text-amber-600 dark:text-amber-400 mt-1">
              {formatCurrency(improvements.reduce((s, i) => s + (Number(i.total_depreciation_to_date) || 0), 0))}
            </div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 font-medium">Prior + current depreciation</div>
          </div>
          <div className="glass-card p-3.5 border border-teal-500/20 bg-teal-500/5">
            <div className="text-[10px] uppercase tracking-wider text-teal-400 font-semibold">Remaining Tax Basis</div>
            <div className="text-lg font-bold tabular-nums text-teal-300 mt-1">
              {formatCurrency(improvements.reduce((s, i) => s + (Number(i.remaining_basis != null ? i.remaining_basis : (i.cost - (i.total_depreciation_to_date || 0))) || 0), 0))}
            </div>
            <div className="text-[10px] text-teal-400/70 mt-0.5">Unrecovered cost basis</div>
          </div>
        </div>

        {improvements.length > 0 ? (
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-[var(--border)] text-[var(--muted)] uppercase tracking-wider text-[10px]">
                  <th className="py-2 px-2">Property</th>
                  <th className="py-2 px-2">Asset Description</th>
                  <th className="py-2 px-2">Category</th>
                  <th className="py-2 px-2">Placed in Service</th>
                  <th className="py-2 px-2 text-right">Cost Basis</th>
                  <th className="py-2 px-2 text-right">Recovery</th>
                  <th className="py-2 px-2 text-right">{taxYear} Depr.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {improvements.slice(0, 5).map((imp) => (
                  <tr key={imp.id} className="hover:bg-white/[0.02]">
                    <td className="py-2.5 px-2 font-medium">{imp.property_name || "—"}</td>
                    <td className="py-2.5 px-2 font-semibold text-[var(--text)]">{imp.description}</td>
                    <td className="py-2.5 px-2 capitalize text-[var(--muted)]">{imp.category}</td>
                    <td className="py-2.5 px-2 tabular-nums text-[var(--muted)]">{imp.date_placed_in_service}</td>
                    <td className="py-2.5 px-2 text-right font-semibold tabular-nums text-[var(--text)]">
                      {formatCurrency(imp.cost)}
                    </td>
                    <td className="py-2.5 px-2 text-right tabular-nums text-[var(--muted)]">
                      {imp.recovery_period_years} yrs
                    </td>
                    <td className="py-2.5 px-2 text-right font-bold tabular-nums text-teal-400">
                      {formatCurrency(imp.annual_depreciation || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {improvements.length > 5 && (
              <div className="text-center pt-3 border-t border-[var(--border)]">
                <Link to="/improvements" className="text-xs text-teal-400 hover:underline">
                  View all {improvements.length} capital improvement assets →
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-5 text-xs text-[var(--muted)] border border-dashed border-[var(--border)] rounded-xl mt-2">
            No capital improvements recorded yet. Log renovations and major appliances to track depreciation and Form 4562 deductions.
          </div>
        )}
      </section>

      {/* CPA-Ready Tax Reports & Custom Exports */}
      <section className="card p-6 mb-6" data-testid="tax-export-card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold flex items-center gap-2"><FileSpreadsheet size={18} /> CPA-Ready Tax Reports</h3>
          <span className="tag tag-teal text-[10px]">IRS Compliant</span>
        </div>
        <p className="text-xs text-slate-600 dark:text-slate-400 mb-4 font-medium leading-relaxed">
          Export categorized transactions, contemporaneously logged business mileage, and itemized Schedule E line items for tax preparation.
        </p>
        <div className="p-4 bg-[var(--surface-hover)]/60 border border-[var(--border)] rounded-2xl mb-4">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-400 mb-2.5">
            Reporting Date Range
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="From Date" type="date" value={from} onChange={setFrom} testId="export-from" />
            <Field label="To Date" type="date" value={to} onChange={setTo} testId="export-to" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <button onClick={exportPdf} data-testid="export-pdf-btn" className="btn-primary text-xs py-2.5 px-3 flex items-center justify-center gap-2">
            <FileDown size={15} /> Schedule E (PDF)
          </button>
          <button onClick={exportMileagePdf} data-testid="export-mileage-pdf-btn" className="btn-secondary text-xs py-2.5 px-3 flex items-center justify-center gap-2">
            <Car size={15} /> Mileage Log (PDF)
          </button>
          <button onClick={exportTxDetailPdf} data-testid="export-tx-detail-pdf-btn" className="btn-secondary text-xs py-2.5 px-3 flex items-center justify-center gap-2">
            <Receipt size={15} /> Transaction Detail (PDF)
          </button>
          <button onClick={exportCsv} data-testid="export-csv-btn" className="btn-secondary text-xs py-2.5 px-3 flex items-center justify-center gap-2">
            <FileSpreadsheet size={15} /> Schedule E (CSV)
          </button>
        </div>
      </section>

      {/* Tenants Management */}
      {can.edit && (
        <section className="card p-6 mb-6" data-testid="tenants-section">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><Users size={18} /> Tenants</h3>
          <form onSubmit={addTenant} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <Field label="Full Name *" value={newTenant.name} onChange={(v) => setNewTenant({ ...newTenant, name: v })} required testId="tenant-name" />
            <Field label="Email" value={newTenant.email} onChange={(v) => setNewTenant({ ...newTenant, email: v })} testId="tenant-email" />
            <Field label="Phone" value={newTenant.phone} onChange={(v) => setNewTenant({ ...newTenant, phone: v })} testId="tenant-phone" />
            <Field label="Rent Due Day (1–31)" type="number" value={newTenant.rent_due_day || 1} onChange={(v) => setNewTenant({ ...newTenant, rent_due_day: parseInt(v) || 1 })} testId="tenant-due-day" />
            <div className="sm:col-span-2">
              <label className="label">Assigned Property</label>
              <select
                className="lapen-input"
                value={newTenant.property_id}
                onChange={(e) => setNewTenant({ ...newTenant, property_id: e.target.value })}
                data-testid="tenant-property"
              >
                <option value="">— None —</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <button type="submit" className="btn-primary text-xs py-2 px-4" data-testid="tenant-add-btn">Add Tenant</button>
            </div>
          </form>

          <div className="divide-y divide-[var(--border)]">
            {tenants.map((t) => (
              <div key={t.id} className="py-3 flex items-center justify-between text-xs sm:text-sm">
                <div>
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-xs text-[var(--muted)]">{t.email || t.phone || "—"} · {t.property_name || "Unassigned"}</div>
                </div>
                {can.delete && (
                  <button onClick={async () => { await api.delete(`/tenants/${t.id}`); loadAll(); }} className="text-[var(--muted)] hover:text-red-400">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Vendors Management */}
      {can.edit && (
        <section className="card p-6 mb-6" data-testid="vendors-section">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><Store size={18} /> Vendors & Contractors</h3>
          <form onSubmit={addVendor} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <Field label="Name *" value={newVendor.name} onChange={(v) => setNewVendor({ ...newVendor, name: v })} required testId="vendor-name" />
            <Field label="Category / Specialty" value={newVendor.category} onChange={(v) => setNewVendor({ ...newVendor, category: v })} placeholder="e.g. Plumber, Electrician, HVAC" testId="vendor-category" />
            <div className="sm:col-span-2">
              <button type="submit" className="btn-primary text-xs py-2 px-4" data-testid="vendor-add-btn">Add Vendor</button>
            </div>
          </form>
          <div className="divide-y divide-[var(--border)]">
            {vendors.map((v) => (
              <div key={v.id} className="py-3 flex items-center justify-between text-xs sm:text-sm">
                <div>
                  <div className="font-semibold">{v.name}</div>
                  <div className="text-xs text-[var(--muted)]">{v.category || "General Contractor"}</div>
                </div>
                {can.delete && (
                  <button onClick={async () => { await api.delete(`/vendors/${v.id}`); loadAll(); }} className="text-[var(--muted)] hover:text-red-400">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Danger Zone */}
      {can.admin && (
        <section className="card p-6 border-red-500/30 bg-red-500/5">
          <h3 className="text-lg font-bold text-red-400 mb-1">Danger Zone</h3>
          <p className="text-xs text-[var(--muted)] mb-4">Wipe data or restore default seed properties.</p>
          <div className="flex gap-3 flex-wrap">
            <button onClick={reseed} data-testid="reseed-btn" className="btn-secondary text-xs py-2 px-4 flex items-center gap-1.5">
              <RefreshCw size={14} /> Restore Demo Data
            </button>
            <button onClick={wipe} data-testid="wipe-btn" className="p-2.5 rounded-xl bg-red-500 text-white font-semibold text-xs flex items-center gap-1.5 hover:bg-red-600 transition-colors">
              <Trash2 size={14} /> Wipe All Data
            </button>
          </div>
        </section>
      )}
    </Layout>
  );
}
